import { useCallback, useState } from "react";
import { type LayoutChangeEvent, Pressable, StyleSheet, useWindowDimensions } from "react-native";

import {
  type AttributeValueFragment,
  type OrderServiceVariantsFragment,
  formatPrice,
} from "@stepapp/core";

import { V2CheckoutBundleServiceVariant } from "@stepapp/core/apiTypes";
import { useBreakpoints } from "../hooks/useBreakpoints";
import { gradientPoint, palette, spacing } from "../styles";
import Block from "./Block";
import Divider from "./Divider";
import Gradient from "./Gradient";
import LinePlaceholder from "./LinePlaceholder";
import SvgIcon, { type IconNames } from "./SvgIcon";
import Typography from "./Typography";

type Props = {
  additionalServices?: (
    | OrderServiceVariantsFragment
    | AttributeValueFragment
    | V2CheckoutBundleServiceVariant
    | null
  )[];
  answer?: string;
  currency?: string;
  description?: string;
  editable?: boolean;
  homeSize?: number;
  homeType?: string | null;
  icon?: IconNames;
  loading?: boolean;
  onEdit?: () => void;
  price?: number | string;
  reverseStyles?: boolean;
  text?: string;
  title?: string;
  titleContinue?: string;
  type?: "address" | "default" | "unstyled" | "worker";
};

const OrderInfoItem = ({
  onEdit,
  currency,
  additionalServices,
  title,
  titleContinue,
  answer,
  loading = false,
  editable = true,
  price = 0,
  description = "",
  icon = "edit",
  type = "default",
  text,
  homeType = "-",
  homeSize,
  reverseStyles = false,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO: refactor
}: Props) => {
  const priceFormatter = formatPrice();
  const { mobileOnly } = useBreakpoints();
  const { width: screenWidth } = useWindowDimensions();
  const applyStyles = type !== "unstyled";

  const [height, setHeight] = useState(0);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setHeight(height);
  }, []);

  const answerType = reverseStyles ? "secondary" : "primary";
  const answerVariant = reverseStyles ? "lightLabel" : type === "worker" ? "newH3" : "newH4";
  const titleType = reverseStyles ? "primary" : type === "address" ? "white" : "secondary";
  const titleVariant = reverseStyles
    ? "newH4"
    : type === "address"
      ? "paragraphBold"
      : type === "worker"
        ? "paragraph"
        : "lightLabel";

  if (loading) {
    return (
      <LinePlaceholder
        borderRadius={1}
        marginBottom={1}
        height={9}
        width={(screenWidth - 40) / 8}
      />
    );
  }

  return (
    <Block
      row
      borderRadius={1}
      marginBottom={1.5}
      minHeight={6}
      onLayout={onLayout}
      paddingLeft={applyStyles ? (mobileOnly ? 1.5 : 2) : 0}
      paddingRight={applyStyles ? (mobileOnly ? 1.75 : 2.25) : 0}
      paddingVertical={type === "address" ? 2.5 : 1.5}
      style={{
        backgroundColor: applyStyles ? palette.common.grayBackground : palette.common.white,
      }}
    >
      <Block justifyCenter flex={1} style={styles.container}>
        {!!text && (
          <Block>
            <Typography variant="lightLabel">{text}</Typography>
          </Block>
        )}

        <Block alignCenter row>
          <Block flex={1}>
            <Typography numberOfLines={2} type={titleType} variant={titleVariant}>
              {title}
            </Typography>

            {!!titleContinue && (
              <Typography
                numberOfLines={2}
                type={type === "address" ? "white" : "secondary"}
                variant={
                  type === "address" ? "paragraphBold" : mobileOnly ? "lightLabel" : "lightLabel"
                }
              >
                {titleContinue}
              </Typography>
            )}
          </Block>

          {(!!price || !!description) && (
            <Block alignEnd justifyCenter marginTop={0.5} width={type === "address" ? 0 : 12.5}>
              <Typography
                type={Number.parseFloat(`${price}`) > 0 ? "primary" : "success"}
                variant={mobileOnly ? "lightLabel" : "paragraph"}
              >
                {description || priceFormatter(price, { currency })}
              </Typography>
            </Block>
          )}
        </Block>

        <Block justifyCenter>
          {additionalServices?.map((item) => (
            <Block alignCenter justifySpace row key={JSON.stringify(item)}>
              <Typography numberOfLines={2} variant={type === "worker" ? "newH3" : "newH4"}>
                {item && "quantity" in item ? `${item?.quantity}x ${item?.title}` : item?.name}
              </Typography>

              {!!price && (
                <Typography type="secondary" variant="lightLabel">
                  {item && "quantity" in item ? `${item?.quantity}x ` : ""}
                  {priceFormatter(item?.price, { currency })}
                </Typography>
              )}
            </Block>
          ))}

          {type === "address" && (
            <Block row>
              <Typography decoration="capitalize" type="white" variant="paragraph">
                {homeType?.toLowerCase()}
              </Typography>
              <Divider spaceless vertical />
              <Typography type="white" variant="paragraph">{`${homeSize}m`}</Typography>
              <Typography type="white" variant="smallParagraph">
                {2}
              </Typography>
            </Block>
          )}

          {!!answer && (
            <Block justifySpace row flex={1} marginVertical={0.5}>
              <Typography type={answerType} variant={answerVariant}>
                {answer}
              </Typography>
            </Block>
          )}
        </Block>
      </Block>

      {editable && onEdit && (
        <Block marginLeft={mobileOnly ? 1.75 : 4} style={styles.container} width={4.5}>
          <Pressable onPress={onEdit} style={styles.editButton}>
            <SvgIcon color={palette.primary.accent} name={icon} size={spacing.unit * 2.25} />
          </Pressable>
        </Block>
      )}

      {type === "address" && (
        <Gradient
          direction="topRight"
          height={height}
          points={gradientPoint.pink}
          style={styles.gradient}
        />
      )}
    </Block>
  );
};

export default OrderInfoItem;

const styles = StyleSheet.create({
  container: {
    zIndex: 10,
  },
  editButton: {
    alignItems: "center",
    backgroundColor: palette.common.white,
    borderRadius: spacing.unit * 2.25,
    height: spacing.unit * 4.5,
    justifyContent: "center",
    width: spacing.unit * 4.5,
  },
  gradient: {
    borderRadius: 10,
  },
});
